import { chunk, concat, filter, has, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import styled from '@emotion/styled';

import { PageWrapper } from '../styles';

import Card from '../components/card';
import Header from '../components/entries/header';
import Answer from '../components/result/answer';

import AnimatedBox from '../components/result/animated_box';
import AnswerBox from '../components/result/answer_box';
import CardsFadeIn from '../components/result/cards_fade_in';
import Grid from '../components/result/grid';
import Subtitle from '../components/result/subtitle';
import Title from '../components/result/title';

import QuestionBubble from '../components/vector/question_bubble';

const ResultPage = ({ question, questionId, userEntries }) => {
  const userEntriesToShow = useMemo(() => {
    const orderedEntries = orderBy(
      // Filter by score above 0
      filter(
        userEntries,
        userEntry =>
          has(userEntry.entries, questionId) &&
          userEntry.entries[questionId].value > 0
      ),

      // Order by highest score
      [`entries.${questionId}.value`],
      'desc'
    );

    let topScore;
    if (orderedEntries.length > 0) {
      topScore = orderedEntries[0].entries[questionId].value;
    } else {
      topScore = 1;
    }

    const [firstChunk, secondChunk] = chunk(
      // filter to top scorers only
      filter(orderedEntries, [`entries[${questionId}].value`, topScore]),
      // Chunk in to groups of three
      3
    );

    let entries;
    if (Array.isArray(secondChunk) && secondChunk.length === 3) {
      entries = concat(firstChunk, secondChunk);
    } else {
      entries = firstChunk;
    }

    return entries;
  }, [questionId, userEntries]);

  const userEntriesToShowLength = userEntriesToShow.length;
  const userEntriesSize = userEntriesToShowLength > 3 ? 'small' : 'large';
  const showUserEntries = userEntriesToShowLength > 0;

  return (
    <PageWrapper>
      <Header />

      <Grid key="wrapper-grid">
        <AnswerBox key="answer-box" size={userEntriesSize}>
          <Title>
            <QuestionBubble height="1.2em" /> {question.question_data.title}
          </Title>

          <Subtitle>
            <Answer question={question} />
          </Subtitle>
        </AnswerBox>

        {showUserEntries ? (
          <CardsFadeIn
            key="cards-fade-in"
            columns={userEntriesToShowLength}
            size={userEntriesSize}
          >
            {userEntriesToShow.map(userEntry => (
              <Card
                key={userEntry.user.id}
                user={userEntry.user}
                points={userEntry.entries[questionId].value}
                size={userEntriesSize}
              />
            ))}
          </CardsFadeIn>
        ) : (
          <NoWinner key="no-winner">
            <Subtitle>
              No one picked this one
              <br />
              Better luck next pick!
            </Subtitle>
          </NoWinner>
        )}
      </Grid>
    </PageWrapper>
  );
};

const NoWinner = styled(AnimatedBox)`
  background: ${props => props.theme.foregroundColor};
  border-radius: 0.5rem;
  box-shadow: 0 0 2.5rem rgba(0, 0, 0, 0.5);

  margin-top: 1em;
  padding-top: 1em;

  width: 100%;

  align-items: center;
  align-self: start;
  justify-content: center;
  justify-items: center;
`;

export default ResultPage;
